import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _createSuper(t) { var r = _isNativeReflectConstruct(); return function () { var e, o = _getPrototypeOf(t); if (r) { var s = _getPrototypeOf(this).constructor; e = Reflect.construct(o, arguments, s); } else e = o.apply(this, arguments); return _possibleConstructorReturn(this, e); }; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useContext, useLayoutEffect, useRef, PureComponent } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { getSchemaBasedOnStage } from '@atlaskit/adf-schema/schema-default';
import { ProviderFactory, ProviderFactoryProvider } from '@atlaskit/editor-common/provider-factory';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { UnsupportedBlock, BaseTheme, WidthProvider, WithCreateAnalyticsEvent, IntlErrorBoundary } from '@atlaskit/editor-common/ui';
import { startMeasure, stopMeasure } from '@atlaskit/editor-common/performance-measures';
import { getAnalyticsAppearance, getAnalyticsEventSeverity, shouldForceTracking } from '@atlaskit/editor-common/utils';
import { getDistortedDurationMonitor } from '@atlaskit/editor-common/performance/measure-render';
import { browser } from '@atlaskit/editor-common/browser';
import { getResponseEndTime } from '@atlaskit/editor-common/performance/navigation';
import { fg } from '@atlaskit/platform-feature-flags';
import { normalizeFeatureFlags } from '@atlaskit/editor-common/normalize-feature-flags';
import { akEditorFullPageDefaultFontSize } from '@atlaskit/editor-shared-styles';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { FabricEditorAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import uuid from 'uuid/v4';
import { ReactSerializer, renderDocument } from '../../';
import { TELEPOINTER_ID, rendererStyles } from './style';
import { TruncatedWrapper } from './truncated-wrapper';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { PLATFORM, MODE } from '../../analytics/events';
import AnalyticsContext from '../../analytics/analyticsContext';
import { Provider as SmartCardStorageProvider } from '../SmartCardStorage';
import { BreakoutSSRInlineScript } from './breakout-ssr';
import { RendererActionsContext, RendererContext as ActionsContext } from '../RendererActionsContext';
import { ActiveHeaderIdProvider } from '../active-header-id-provider';
import { AnnotationsPositionContext, AnnotationsWrapper } from '../annotations';
import { getActiveHeadingId, isNestedHeaderLinksEnabled } from '../../react/utils/links';
import { findInTree } from '../../utils';
import { isInteractiveElement } from './click-to-edit';
import { useRendererContext, RendererContextProvider } from '../../renderer-context';
import memoizeOne from 'memoize-one';
import { ErrorBoundary } from './ErrorBoundary';
import { EditorMediaClientProvider } from '../../react/utils/EditorMediaClientProvider';
import { nodeToReact } from '../../react/nodes';
import { countNodes } from './count-nodes';
export var NORMAL_SEVERITY_THRESHOLD = 2000;
export var DEGRADED_SEVERITY_THRESHOLD = 3000;
var packageName = "@atlaskit/renderer";
var packageVersion = "112.5.3";
var setAsQueryContainerStyles = css({
  containerName: 'ak-renderer-wrapper',
  containerType: 'inline-size',
  contain: 'layout style inline-size'
});
export var defaultNodeComponents = nodeToReact;

/**
 * Exported due to enzyme test reliance on this component.
 */
export var __RendererClassComponent = /*#__PURE__*/function (_PureComponent) {
  _inherits(__RendererClassComponent, _PureComponent);
  var _super = _createSuper(__RendererClassComponent);
  function __RendererClassComponent(props) {
    var _this;
    _classCallCheck(this, __RendererClassComponent);
    _this = _super.call(this, props);
    /**
     * This is used in measuring the Renderer Mount time and is then
     * deleted once that measurement occurs.
     */
    _defineProperty(_assertThisInitialized(_this), "renderedMeasurementDistortedDurationMonitor", getDistortedDurationMonitor());
    _defineProperty(_assertThisInitialized(_this), "createRendererContext", memoizeOne(function (featureFlags, isTopLevelRenderer) {
      var normalizedFeatureFlags = normalizeFeatureFlags(featureFlags);
      return {
        featureFlags: normalizedFeatureFlags,
        // The context is uninitialized at the top level. In nested levels it's all false
        isTopLevelRenderer: isTopLevelRenderer === undefined
      };
    }));
    _defineProperty(_assertThisInitialized(_this), "fireAnalyticsEvent", function (event) {
      var createAnalyticsEvent = _this.props.createAnalyticsEvent;
      if (createAnalyticsEvent) {
        var channel = FabricChannel.editor;
        createAnalyticsEvent(event).fire(channel);
      }
    });
    _defineProperty(_assertThisInitialized(_this), "getSchema", memoizeOne(function (schema, adfStage) {
      if (schema) {
        return schema;
      }
      return getSchemaBasedOnStage(adfStage);
    }));
    _defineProperty(_assertThisInitialized(_this), "onMouseDownEditView", function () {
      // When the user is deselecting text on the screen by clicking, if they are clicking outside
      // the current selection, by the time the onclick handler is called the window.getSelection()
      // value will already be cleared.
      // The mousedown callback is called before the selection is cleared.
      var windowSelection = window.getSelection();
      _this.mouseDownSelection = windowSelection !== null ? windowSelection.toString() : undefined;
    });
    _defineProperty(_assertThisInitialized(_this), "handleMouseTripleClickInTables", function (event) {
      var _parentElement, _parentElement2;
      if (browser.ios || browser.android) {
        return;
      }
      var badBrowser = browser.chrome || browser.safari;
      var tripleClick = event.detail >= 3;
      if (!(badBrowser && tripleClick)) {
        return;
      }
      var selection = window.getSelection();
      if (!selection) {
        return;
      }
      var type = selection.type,
        anchorNode = selection.anchorNode,
        focusNode = selection.focusNode;
      var rangeSelection = Boolean(type === 'Range' && anchorNode && focusNode);
      if (!rangeSelection) {
        return;
      }
      var target = event.target;
      var tableCell = target.closest('td,th');
      var clickedInCell = Boolean(tableCell);
      if (!clickedInCell) {
        return;
      }
      var anchorInCell = tableCell.contains(anchorNode);
      var focusInCell = tableCell.contains(focusNode);
      var selectionStartsOrEndsOutsideClickedCell = !(anchorInCell && focusInCell);
      if (!selectionStartsOrEndsOutsideClickedCell) {
        return;
      }
      // If selection starts or ends in a different cell than the clicked cell,
      // we select the node inside the clicked cell (or if both are in a different
      // cell, we select the cell's contents instead). We want to select the nearest
      // parent block, so that a whole line of text/content is selected (rather than
      // selecting a span that would select one specific chunk of text).
      var elementToSelect = anchorInCell ? (_parentElement = anchorNode.parentElement) === null || _parentElement === void 0 ? void 0 : _parentElement.closest('div,p') : focusInCell ? (_parentElement2 = focusNode.parentElement) === null || _parentElement2 === void 0 ? void 0 : _parentElement2.closest('div,p') : tableCell;
      if (elementToSelect) {
        selection.selectAllChildren(elementToSelect);
      }
    });
    _this.providerFactory = props.dataProviders || new ProviderFactory();
    _this.serializer = new ReactSerializer(_this.deriveSerializerProps(props));
    _this.editorRef = props.innerRef || /*#__PURE__*/React.createRef();
    _this.id = uuid();
    startMeasure("Renderer Render Time: ".concat(_this.id));
    return _this;
  }
  _createClass(__RendererClassComponent, [{
    key: "anchorLinkAnalytics",
    value: function anchorLinkAnalytics() {
      var hash = window.location.hash && decodeURIComponent(window.location.hash.slice(1));
      var disableHeadingIDs = this.props.disableHeadingIDs;
      if (!disableHeadingIDs && hash && this.editorRef && this.editorRef.current instanceof HTMLElement) {
        var anchorLinkElement = document.getElementById(hash);
        // We are not use this.editorRef.querySelector here, instead we have this.editorRef.contains
        // because querySelector might fail if there are special characters in hash, and CSS.escape is still experimental.
        if (anchorLinkElement && this.editorRef.current.contains(anchorLinkElement)) {
          this.fireAnalyticsEvent({
            action: ACTION.VIEWED,
            actionSubject: ACTION_SUBJECT.ANCHOR_LINK,
            attributes: {
              platform: PLATFORM.WEB,
              mode: MODE.RENDERER
            },
            eventType: EVENT_TYPE.UI
          });
        }
      }
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this2 = this;
      this.fireAnalyticsEvent({
        action: ACTION.STARTED,
        actionSubject: ACTION_SUBJECT.RENDERER,
        attributes: {
          platform: PLATFORM.WEB
        },
        eventType: EVENT_TYPE.UI
      });
      this.rafID = requestAnimationFrame(function () {
        stopMeasure("Renderer Render Time: ".concat(_this2.id), function (duration) {
          var _analyticsEventSeveri, _analyticsEventSeveri2, _this2$props;
          var analyticsEventSeverityTracking = _this2.props.analyticsEventSeverityTracking;
          var forceSeverityTracking = typeof analyticsEventSeverityTracking === 'undefined' && shouldForceTracking();
          var severity = !!forceSeverityTracking || analyticsEventSeverityTracking !== null && analyticsEventSeverityTracking !== void 0 && analyticsEventSeverityTracking.enabled ? getAnalyticsEventSeverity(duration, (_analyticsEventSeveri = analyticsEventSeverityTracking === null || analyticsEventSeverityTracking === void 0 ? void 0 : analyticsEventSeverityTracking.severityNormalThreshold) !== null && _analyticsEventSeveri !== void 0 ? _analyticsEventSeveri : NORMAL_SEVERITY_THRESHOLD, (_analyticsEventSeveri2 = analyticsEventSeverityTracking === null || analyticsEventSeverityTracking === void 0 ? void 0 : analyticsEventSeverityTracking.severityDegradedThreshold) !== null && _analyticsEventSeveri2 !== void 0 ? _analyticsEventSeveri2 : DEGRADED_SEVERITY_THRESHOLD) : undefined;

          // ED-16320: Check for explicit disable so that by default
          // it will still be enabled as it currently is. Then we can
          // progressively opt out synthetic tenants.
          var isTTRTrackingExplicitlyDisabled = ((_this2$props = _this2.props) === null || _this2$props === void 0 || (_this2$props = _this2$props.analyticsEventSeverityTracking) === null || _this2$props === void 0 ? void 0 : _this2$props.enabled) === false;
          if (!isTTRTrackingExplicitlyDisabled) {
            _this2.fireAnalyticsEvent({
              action: ACTION.RENDERED,
              actionSubject: ACTION_SUBJECT.RENDERER,
              attributes: {
                platform: PLATFORM.WEB,
                duration: duration,
                distortedDuration: _this2.renderedMeasurementDistortedDurationMonitor.distortedDuration,
                ttfb: getResponseEndTime(),
                nodes: countNodes(_this2.props.document),
                severity: severity
              },
              eventType: EVENT_TYPE.OPERATIONAL
            });
          }
          _this2.renderedMeasurementDistortedDurationMonitor.cleanup();
          delete _this2.renderedMeasurementDistortedDurationMonitor;
        });
        _this2.anchorLinkAnalytics();
      });
    }
  }, {
    key: "UNSAFE_componentWillReceiveProps",
    value: function UNSAFE_componentWillReceiveProps(nextProps) {
      var nextMedia = nextProps.media || {};
      var media = this.props.media || {};
      if (nextProps.portal !== this.props.portal || nextProps.appearance !== this.props.appearance || nextProps.stickyHeaders !== this.props.stickyHeaders || nextProps.disableActions !== this.props.disableActions || nextProps.allowCustomPanels !== this.props.allowCustomPanels || nextProps.extensionHandlers !== this.props.extensionHandlers || nextProps.allowHeadingAnchorLinks !== this.props.allowHeadingAnchorLinks || nextMedia.allowLinking !== media.allowLinking) {
        this.serializer = new ReactSerializer(this.deriveSerializerProps(nextProps));
      }
    }
  }, {
    key: "deriveSerializerProps",
    value: function deriveSerializerProps(props) {
      // if just passed a boolean, change shape into object to simplify type
      var stickyHeaders = props.stickyHeaders ? props.stickyHeaders === true ? {} : props.stickyHeaders : undefined;
      var annotationProvider = props.annotationProvider;
      var allowAnnotationsDraftMode = Boolean(annotationProvider && annotationProvider.inlineComment && annotationProvider.inlineComment.allowDraftMode);
      var _this$createRendererC = this.createRendererContext(props.featureFlags, props.isTopLevelRenderer),
        featureFlags = _this$createRendererC.featureFlags;
      return {
        startPos: props.startPos,
        providers: this.providerFactory,
        eventHandlers: props.eventHandlers,
        extensionHandlers: props.extensionHandlers,
        portal: props.portal,
        objectContext: _objectSpread({
          adDoc: props.document,
          schema: props.schema
        }, props.rendererContext),
        appearance: props.appearance,
        disableHeadingIDs: props.disableHeadingIDs,
        disableActions: props.disableActions,
        allowHeadingAnchorLinks: props.allowHeadingAnchorLinks,
        allowColumnSorting: props.allowColumnSorting,
        fireAnalyticsEvent: this.fireAnalyticsEvent,
        shouldOpenMediaViewer: props.shouldOpenMediaViewer,
        allowAltTextOnImages: props.allowAltTextOnImages,
        stickyHeaders: stickyHeaders,
        allowMediaLinking: props.media && props.media.allowLinking,
        surroundTextNodesWithTextWrapper: allowAnnotationsDraftMode,
        media: props.media,
        emojiResourceConfig: props.emojiResourceConfig,
        smartLinks: props.smartLinks,
        allowCopyToClipboard: props.allowCopyToClipboard,
        allowWrapCodeBlock: props.allowWrapCodeBlock,
        allowCustomPanels: props.allowCustomPanels,
        allowAnnotations: props.allowAnnotations,
        allowSelectAllTrap: props.allowSelectAllTrap,
        allowPlaceholderText: props.allowPlaceholderText,
        nodeComponents: props.nodeComponents,
        // does not currently support SSR, should not be enabled in environments where Renderer is SSR-ed
        allowWindowedCodeBlock: featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.allowWindowedCodeBlock,
        isInsideOfInlineExtension: props.isInsideOfInlineExtension,
        textHighlighter: props.UNSTABLE_textHighlighter,
        allowTableAlignment: props.UNSTABLE_allowTableAlignment,
        allowTableResizing: props.UNSTABLE_allowTableResizing
      };
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;
      var _this$props = this.props,
        adfDocument = _this$props.document,
        onComplete = _this$props.onComplete,
        onError = _this$props.onError,
        appearance = _this$props.appearance,
        allowAnnotations = _this$props.allowAnnotations,
        adfStage = _this$props.adfStage,
        truncated = _this$props.truncated,
        maxHeight = _this$props.maxHeight,
        fadeOutHeight = _this$props.fadeOutHeight,
        enableSsrInlineScripts = _this$props.enableSsrInlineScripts,
        allowHeadingAnchorLinks = _this$props.allowHeadingAnchorLinks,
        allowPlaceholderText = _this$props.allowPlaceholderText,
        allowColumnSorting = _this$props.allowColumnSorting,
        allowCopyToClipboard = _this$props.allowCopyToClipboard,
        allowWrapCodeBlock = _this$props.allowWrapCodeBlock,
        allowCustomPanels = _this$props.allowCustomPanels,
        media = _this$props.media;
      var rendererContext = this.createRendererContext(this.props.featureFlags, this.props.isTopLevelRenderer);
      var allowNestedHeaderLinks = isNestedHeaderLinksEnabled(allowHeadingAnchorLinks);
      /**
       * Handle clicks inside renderer. If the click isn't on media, in the media picker, or on a
       * link, call the onUnhandledClick eventHandler (which in Jira for example, may switch the
       * renderer out for the editor).
       * @param event Click event anywhere inside renderer
       */
      var handleWrapperOnClick = function handleWrapperOnClick(event) {
        var _this3$props$eventHan;
        var targetElement = event.target;

        // ED-14862: When a user triple clicks to select a line of content inside a
        // a table cell, but the browser incorrectly moves the selection start or end into
        // a different table cell, we manually set the selection back to within the original
        // table cell the user intended to target
        _this3.handleMouseTripleClickInTables(event);
        if (!((_this3$props$eventHan = _this3.props.eventHandlers) !== null && _this3$props$eventHan !== void 0 && _this3$props$eventHan.onUnhandledClick)) {
          return;
        }
        if (!(targetElement instanceof window.Element)) {
          return;
        }
        var rendererWrapper = event.currentTarget;

        // Check if the click was on an interactive element
        var isInteractiveElementInTree = findInTree(targetElement, rendererWrapper, isInteractiveElement);
        if (isInteractiveElementInTree) {
          return;
        }

        // Ensure that selecting text in the renderer doesn't trigger onUnhandledClick
        // This logic originated in jira-frontend:
        // src/packages/issue/issue-view/src/views/field/rich-text/rich-text-inline-edit-view.js

        // The selection is required to be checked in `onMouseDown` and here. If not here, a new
        // selection isn't reported; if not in `onMouseDown`, a click outside the selection will
        // return an empty selection, which will erroneously fire onUnhandledClick.
        var windowSelection = window.getSelection();
        var selection = windowSelection !== null ? windowSelection.toString() : undefined;
        var hasSelection = selection && selection.length !== 0;
        var hasSelectionMouseDown = _this3.mouseDownSelection && _this3.mouseDownSelection.length !== 0;
        var allowEditBasedOnSelection = !hasSelection && !hasSelectionMouseDown;
        if (allowEditBasedOnSelection) {
          _this3.props.eventHandlers.onUnhandledClick(event);
        }
      };
      try {
        var _rendererContext$feat;
        var schema = this.getSchema(this.props.schema, this.props.adfStage);
        var _renderDocument = renderDocument(adfDocument, this.serializer, schema, adfStage, this.props.useSpecBasedValidator, this.id, this.fireAnalyticsEvent, this.props.unsupportedContentLevelsTracking, this.props.appearance, this.props.includeNodesCountInStats),
          result = _renderDocument.result,
          stat = _renderDocument.stat,
          pmDoc = _renderDocument.pmDoc;
        if (onComplete) {
          onComplete(stat);
        }
        var rendererOutput = jsx(RendererContextProvider, {
          value: rendererContext
        }, jsx(ActiveHeaderIdProvider, {
          value: getActiveHeadingId(allowHeadingAnchorLinks)
        }, jsx(AnalyticsContext.Provider, {
          value: {
            fireAnalyticsEvent: function fireAnalyticsEvent(event) {
              return _this3.fireAnalyticsEvent(event);
            }
          }
        }, jsx(SmartCardStorageProvider, null, jsx(ProviderFactoryProvider, {
          value: this.providerFactory
        }, jsx(RendererWrapper, {
          allowAnnotations: allowAnnotations,
          appearance: appearance,
          allowNestedHeaderLinks: allowNestedHeaderLinks,
          allowColumnSorting: allowColumnSorting,
          allowCopyToClipboard: allowCopyToClipboard,
          allowWrapCodeBlock: allowWrapCodeBlock,
          allowCustomPanels: allowCustomPanels,
          allowPlaceholderText: allowPlaceholderText,
          useBlockRenderForCodeBlock: (_rendererContext$feat = rendererContext.featureFlags.useBlockRenderForCodeBlock) !== null && _rendererContext$feat !== void 0 ? _rendererContext$feat : true,
          addTelepointer: this.props.addTelepointer,
          innerRef: this.editorRef,
          onClick: handleWrapperOnClick,
          onMouseDown: this.onMouseDownEditView,
          ssr: media === null || media === void 0 ? void 0 : media.ssr,
          isInsideOfInlineExtension: this.props.isInsideOfInlineExtension,
          isTopLevelRenderer: rendererContext.isTopLevelRenderer
        }, enableSsrInlineScripts ? jsx(BreakoutSSRInlineScript, null) : null, jsx(RendererActionsInternalUpdater, {
          doc: pmDoc,
          schema: schema,
          onAnalyticsEvent: this.fireAnalyticsEvent
        }, result)))))));
        var rendererResult = truncated ? jsx(TruncatedWrapper, {
          height: maxHeight,
          fadeHeight: fadeOutHeight
        }, rendererOutput) : rendererOutput;
        return jsx(Fragment, null, rendererResult);
      } catch (e) {
        var _rendererContext$feat2;
        if (onError) {
          onError(e);
        }
        return jsx(RendererWrapper, {
          allowAnnotations: allowAnnotations,
          appearance: appearance,
          allowCopyToClipboard: allowCopyToClipboard,
          allowWrapCodeBlock: allowWrapCodeBlock,
          allowPlaceholderText: allowPlaceholderText,
          allowColumnSorting: allowColumnSorting,
          allowNestedHeaderLinks: allowNestedHeaderLinks,
          useBlockRenderForCodeBlock: (_rendererContext$feat2 = rendererContext.featureFlags.useBlockRenderForCodeBlock) !== null && _rendererContext$feat2 !== void 0 ? _rendererContext$feat2 : true,
          addTelepointer: this.props.addTelepointer,
          onClick: handleWrapperOnClick,
          isTopLevelRenderer: rendererContext.isTopLevelRenderer
        }, jsx(UnsupportedBlock, null));
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      var dataProviders = this.props.dataProviders;
      if (this.rafID) {
        window.cancelAnimationFrame(this.rafID);
      }

      // if this is the ProviderFactory which was created in constructor
      // it's safe to destroy it on Renderer unmount
      if (!dataProviders) {
        this.providerFactory.destroy();
      }
    }
  }]);
  return __RendererClassComponent;
}(PureComponent);
export function Renderer(props) {
  var _React$useContext = React.useContext(AnnotationsPositionContext),
    startPos = _React$useContext.startPos;
  var _useRendererContext = useRendererContext(),
    isTopLevelRenderer = _useRendererContext.isTopLevelRenderer;
  return (
    // eslint-disable-next-line react/jsx-pascal-case
    jsx(__RendererClassComponent, _extends({}, props, {
      startPos: startPos,
      isTopLevelRenderer: isTopLevelRenderer
    }))
  );
}

// Usage notes:
// Used by Confluence for View page renderer
// For the nested renderers - see RendererWithAnnotationSelection.
export var RendererWithAnalytics = /*#__PURE__*/React.memo(function (props) {
  return jsx(FabricEditorAnalyticsContext, {
    data: {
      appearance: getAnalyticsAppearance(props.appearance),
      packageName: packageName,
      packageVersion: packageVersion,
      componentName: 'renderer',
      editorSessionId: uuid()
    }
  }, jsx(WithCreateAnalyticsEvent, {
    render: function render(createAnalyticsEvent) {
      // `IntlErrorBoundary` only captures Internationalisation errors, leaving others for `ErrorBoundary`.
      return jsx(ErrorBoundary, {
        component: ACTION_SUBJECT.RENDERER,
        rethrowError: true,
        fallbackComponent: null,
        createAnalyticsEvent: createAnalyticsEvent
      }, jsx(IntlErrorBoundary, null, jsx(Renderer, _extends({}, props, {
        createAnalyticsEvent: createAnalyticsEvent
      }))));
    }
  }));
});
var RendererWrapper = /*#__PURE__*/React.memo(function (props) {
  var allowColumnSorting = props.allowColumnSorting,
    allowNestedHeaderLinks = props.allowNestedHeaderLinks,
    innerRef = props.innerRef,
    appearance = props.appearance,
    children = props.children,
    onClick = props.onClick,
    onMouseDown = props.onMouseDown,
    useBlockRenderForCodeBlock = props.useBlockRenderForCodeBlock,
    addTelepointer = props.addTelepointer,
    ssr = props.ssr,
    isInsideOfInlineExtension = props.isInsideOfInlineExtension,
    allowTableResizing = props.allowTableResizing,
    isTopLevelRenderer = props.isTopLevelRenderer;
  var createTelepointer = function createTelepointer() {
    var telepointer = document.createElement('span');
    telepointer.textContent = "\u200B";
    telepointer.id = TELEPOINTER_ID;
    return telepointer;
  };
  var initialUpdate = React.useRef(true);
  React.useEffect(function () {
    // We must check if window is defined, if it isn't we are in a SSR environment
    // and we don't want to add the telepointer
    if (typeof window !== 'undefined' && addTelepointer && innerRef !== null && innerRef !== void 0 && innerRef.current) {
      var _renderer = innerRef.current.querySelector('.ak-renderer-document');
      if (initialUpdate.current) {
        var lastChild = _renderer.lastChild;
        lastChild && lastChild.appendChild(createTelepointer());
      }
      var mutateTelepointer = function mutateTelepointer(mutations) {
        mutations.forEach(function (mutation) {
          if (initialUpdate.current) {
            var oldTelepointer = _renderer.querySelector("#".concat(TELEPOINTER_ID));
            if (oldTelepointer) {
              oldTelepointer.remove();
            }
            var _lastChild = _renderer.lastChild;
            _lastChild && _lastChild.appendChild(createTelepointer());
            initialUpdate.current = false;
          }
          if (mutation.type === 'characterData') {
            var parentNode = mutation.target.parentElement;
            if (parentNode) {
              var _oldTelepointer = _renderer.querySelector("#".concat(TELEPOINTER_ID));
              if (_oldTelepointer) {
                _oldTelepointer.remove();
              }
              parentNode.appendChild(createTelepointer());
            }
          }
        });
      };
      var observer = new MutationObserver(mutateTelepointer);
      observer.observe(innerRef.current, {
        characterData: true,
        attributes: false,
        childList: true,
        subtree: true
      });
      return function () {
        return observer.disconnect();
      };
    }
  }, [innerRef, addTelepointer]);
  var renderer = jsx(WidthProvider
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
  , {
    className: "ak-renderer-wrapper is-".concat(appearance),
    "data-appearance": appearance,
    shouldCheckExistingValue: isInsideOfInlineExtension
  }, jsx(BaseTheme, {
    baseFontSize: appearance && appearance !== 'comment' ? akEditorFullPageDefaultFontSize : undefined
  }, jsx(EditorMediaClientProvider, {
    ssr: ssr
  }, jsx("div", {
    ref: innerRef,
    onClick: onClick,
    onMouseDown: onMouseDown
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
    ,
    css: rendererStyles({
      appearance: appearance,
      allowNestedHeaderLinks: allowNestedHeaderLinks,
      allowColumnSorting: !!allowColumnSorting,
      useBlockRenderForCodeBlock: useBlockRenderForCodeBlock,
      allowAnnotations: props.allowAnnotations,
      allowTableResizing: allowTableResizing
    })
  }, children))));

  // We can only make the wrapper div query container when we have a known width.
  // This is also required for SSR to work correctly. As WidthProvider/WithConsumer will not have the correct width during SSR.
  //
  // We are setting this wrapper div as query container conditionally.
  // Only apply container-type = inline-size when having a known width in full-page/full-width/comment mode.
  // Otherwise when appearance is unspecified the renderer size is decided by the content.
  // In this case we can't set the container-type = inline-size as it will collapse width to 0.
  return (appearance === 'full-page' || appearance === 'full-width' || appearance === 'comment') &&
  // In case of having excerpt-include on page there are multiple renderers nested.
  // Make sure only the root renderer is set to be query container.
  isTopLevelRenderer && fg('platform-fix-table-ssr-resizing') ? jsx("div", {
    css: setAsQueryContainerStyles
  }, renderer) : renderer;
});
var RootRendererContext = /*#__PURE__*/React.createContext(null);
function RendererActionsInternalUpdater(_ref) {
  var children = _ref.children,
    doc = _ref.doc,
    schema = _ref.schema,
    onAnalyticsEvent = _ref.onAnalyticsEvent;
  var rootRendererContextValue = React.useContext(RootRendererContext);
  var actions = useContext(ActionsContext);
  var rendererRef = useRef(null);

  // This doc is used by the renderer actions when applying comments to the document.
  // (via hand crafted steps based on non prosemirror based position calculations)
  // It is set to the root renderer's doc as otherwise the resulting document will
  // be incorrect (nested renderers use a fake document which represents a subset
  // of the actual document).
  var _doc;
  if (editorExperiment('comment_on_bodied_extensions', true) && rootRendererContextValue) {
    // If rootRendererContextValue is set -- we are inside a nested renderer
    // and should always use the doc from the root renderer
    _doc = rootRendererContextValue.doc;
  } else {
    // If rootRendererContextValue is not set -- we are in the root renderer
    // and set the doc to the current doc.
    _doc = doc;
  }
  useLayoutEffect(function () {
    if (_doc) {
      actions._privateRegisterRenderer(rendererRef, _doc, schema, onAnalyticsEvent);
    } else {
      actions._privateUnregisterRenderer();
    }
    return function () {
      return actions._privateUnregisterRenderer();
    };
  }, [actions, schema, _doc, onAnalyticsEvent]);
  if (editorExperiment('comment_on_bodied_extensions', true)) {
    return jsx(RootRendererContext.Provider, {
      value: {
        doc: _doc
      }
    }, children);
  }
  return children;
}

// Usage notes:
// Used by Confluence for nested renderers
// For the View page renderer - see RendererWithAnalytics
var RendererWithAnnotationSelection = function RendererWithAnnotationSelection(props) {
  var allowAnnotations = props.allowAnnotations,
    adfDocument = props.document;
  var localRef = React.useRef(null);
  var innerRef = props.innerRef || localRef;
  if (!allowAnnotations) {
    return jsx(RendererWithAnalytics, _extends({
      innerRef: innerRef
    }, props));
  }
  return jsx(RendererActionsContext, null, jsx(AnnotationsWrapper, {
    rendererRef: innerRef,
    adfDocument: adfDocument,
    annotationProvider: props.annotationProvider,
    isNestedRender: true
  }, jsx(RendererWithAnalytics, _extends({
    innerRef: innerRef
  }, props, {
    featureFlags: props.featureFlags
  }))));
};

/* @deprecated using this version of the renderer causes the RendererActions to inaccessible from any consumers */
export default RendererWithAnnotationSelection;